import {
  Tooltip,
  Text,
  type TextProps,
  type TooltipProps,
} from '@chakra-ui/react';

import React, {
  cloneElement,
  memo,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from 'react';
import { KText } from '@/ui-library/components/text';

type TooltipPlacement = NonNullable<TooltipProps['placement']>;

export interface TooltipTextProps extends Omit<TextProps, 'size' | 'position'> {
  text: string;
  stringLength?: number;
  /** Passed to Chakra `Tooltip` as `placement` (not Text CSS `position`). */
  position?: TooltipPlacement;
  variant?: string;
  size?: string;
  tooltipProps?: Partial<TooltipProps>;
}

export const TooltipText = memo(function TooltipText({
  text,
  stringLength = 10,
  position = 'bottom',
  variant = undefined,
  size = undefined,
  tooltipProps,
  ...props
}: TooltipTextProps) {
  if (!text) return null;
  if (text.length <= stringLength)
    return (
      <Text size={size} {...props}>
        {text}
      </Text>
    );
  const displayText = text.substring(0, stringLength);
  const showElipsis = text.length > stringLength;
  return (
    <Tooltip
      label={text}
      placement={position}
      borderRadius="6px"
      variant={variant}
      {...tooltipProps}
    >
      <Text size={size} {...props}>
        {displayText}
        {showElipsis && '...'}
      </Text>
    </Tooltip>
  );
});

TooltipText.displayName = 'TooltipText';

type KTextSansRefChildren = Omit<
  ComponentProps<typeof KText>,
  'children' | 'ref'
>;
type KTextSpreadProps = Partial<Omit<KTextSansRefChildren, 'tooltipProps'>>;

export interface TruncatedTextWithTooltipProps extends KTextSpreadProps {
  text?: string;
  placement?: TooltipPlacement;
  toolTipVariant?: string;
  enableTooltip?: boolean;
  renderText?: (_text: string) => ReactNode;
  children?: ReactElement;
  /** Spread onto the Chakra `Tooltip` (e.g. `hasArrow`, `color`). */
  tooltipProps?: Partial<TooltipProps>;
  /** Spread onto inner `KText` after other text props (e.g. `size`, `color`). */
  textProps?: KTextSpreadProps;
}

export const TruncatedTextWithTooltip = memo(function TruncatedTextWithTooltip({
  text = '',
  placement = 'bottom',
  toolTipVariant = undefined,
  size = undefined,
  enableTooltip: _enableTooltip = true,
  renderText = undefined,
  children = undefined,
  tooltipProps = undefined,
  textProps = undefined,
  ...props
}: TruncatedTextWithTooltipProps) {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;

    const measure = () =>
      setIsTextTruncated(
        element ? element.scrollWidth > element.clientWidth : false
      );

    measure();

    if (!element) return undefined;

    // CORE-5021: one measurement keyed on `text` was not enough. Inside a drawer
    // that animates open the first read lands on a zero-width box, records "fits",
    // and never re-checks — so a clipped name shows its ellipsis with nothing on
    // hover. Re-measure whenever the box actually changes size.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  /**
   * Only placement. The default branch used to hard-code `#161616` / 3px / 8px,
   * which overrode the theme and gave this component TWO looks: near-black with a
   * 3px radius by default, and the themed `secondary-950` at 6px whenever a caller
   * happened to pass `toolTipVariant`. Same component, same page, two tooltips.
   *
   * The theme (`theme/components/ToolTip`) is the one source now, and it matches
   * kula's `TooltipContent` (`bg-secondary-950`, 6px) to the byte — which matters
   * because a card renders both.
   */
  const enableProps: Partial<TooltipProps> = useMemo(
    () => (toolTipVariant ? {} : { placement }),
    [toolTipVariant, placement]
  );

  return (
    <Tooltip
      label={isTextTruncated ? text : ''}
      hasArrow
      {...enableProps}
      variant={toolTipVariant}
      zIndex={99999}
      {...tooltipProps}
    >
      {children ? (
        <div>
          {cloneElement(children, {
            ref: textRef,
          } as Partial<React.HTMLAttributes<HTMLElement>>)}
        </div>
      ) : (
        <KText
          enableTooltip={false}
          size={size}
          ref={textRef}
          isTruncated
          as={renderText && isTextTruncated ? 'span' : undefined}
          {...props}
          {...textProps}
        >
          {renderText ? renderText(text) : text}
        </KText>
      )}
    </Tooltip>
  );
});

TruncatedTextWithTooltip.displayName = 'TruncatedTextWithTooltip';
